import React, { useEffect, useState } from 'react';
import { EUserType } from '../data_models/enums';
import { BRAND_ASSETS } from '../config/defaultAssetUrls';
import useCurrentUser from '../hooks/useCurrentUser';
import useRouting from '../hooks/useRouting';
import { useSearchParams } from 'react-router-dom';
import { countriesToTextMapper, ECountry } from '../data_models/data/countries';
import { EmployerType, IEmployer } from '../data_models/employer';
import useAuth from '../hooks/useAuth';
import { IFreelancer } from '../data_models/freelancer';
import TextInputField from '../components/common/TextInputField';
import { FilledButton } from '../components/common/Button';
import { LOGIN_ROUTE } from '../App';
import { FlatDropdownMenu } from '../components/common/DropdownMenu';
import { getNumericEnumValues, getStringEnumValues } from '../utils/enumUtils';
import { UserEnumTextMapper } from '../data_models/enumToTextMapper';
import { validPassword } from '../utils/regularExpressions';
import { isEmail } from '../utils/regularExpressions';

const SignupPage: React.FC = () => {

  const [searchParams] = useSearchParams();
  const type = (searchParams.get('type') as EUserType) || EUserType.FREELANCER;
  const { signupFreelancer, signupEmployer } = useAuth();
  const { currentUser, currentUserType, updateCurrentUser } = useCurrentUser();
  const { goToSignupUpdateProfile } = useRouting();

  const [userType, setUserType] = useState<EUserType | undefined>(type);
  const [loading, setLoading] = useState<boolean>(false);
  const [updateUserObj, setUpdateUserObj] = useState<IEmployer | IFreelancer | undefined>(undefined);

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [country, setCountry] = useState<ECountry>(ECountry.UNITED_STATES);

  const [employerName, setEmployerName] = useState<string>('');
  const [employerType, setEmployerType] = useState<EmployerType>(EmployerType.COMPANY);

  const [freelancerFirstName, setFreelancerFirstName] = useState<string>('');
  const [freelancerLastName, setFreelancerLastName] = useState<string>('');

  const [errorServerFeedback, setErrorServerFeedback] = useState<string>('');
  const [errorEmail, setErrorEmail] = useState<string>('');
  const [errorPassword, setErrorPassword] = useState<string>('');
  const [errorConfirmPassword, setErrorConfirmPassword] = useState<string>('');
  const [errorFreelancerFirstName, setErrorFreelancerFirstName] = useState<string>('');
  const [errorFreelancerLastName, setErrorFreelancerLastName] = useState<string>('');
  const [errorEmployerName, setErrorEmployerName] = useState<string>('');
  const [errorEmployerType, setErrorEmployerType] = useState<string>('');

  const validateFields = () => {
    let valid = true;

    // check email
    if (!email) {
      setErrorEmail('Email is required');
      valid = false;
    } else {
      if (!isEmail(email)) {
        setErrorEmail('Invalid email address');
        valid = false;
      }
    }

    // check password
    if (!password) {
      setErrorPassword('Password is required');
      valid = false;
    } else {
      if (!validPassword(password)) {
        setErrorPassword('Password requirements: 8 characters, at least 1 character and 1 number');
        valid = false;
      }
    }

    // check confirm password
    if (!confirmPassword) {
      setErrorConfirmPassword('Confirm password is required');
      valid = false;
    } else if (confirmPassword !== password) {
      setErrorConfirmPassword('Passwords do not match');
      valid = false;
    }

    if (userType === EUserType.EMPLOYER) {
      // check employer name
      if (!employerName) {
        setEmployerName('Name is required');
        valid = false;
      } else if (employerName.length < 2) {
        setEmployerName('Name must be at least 2 characters');
        valid = false;
      }
    }

    if (userType === EUserType.FREELANCER) {
      // check first name
      if (!freelancerFirstName) {
        setErrorFreelancerFirstName('First name is required');
        valid = false;
      } else if (freelancerFirstName.length < 2) {
        setErrorFreelancerFirstName('First name must be at least 2 characters');
        valid = false;
      }

      // check last name
      if (!freelancerLastName) {
        setErrorFreelancerLastName('Last name is required');
        valid = false;
      } else if (freelancerLastName.length < 2) {
        setErrorFreelancerLastName('Last name must be at least 2 characters');
        valid = false;
      }
    }

    return valid;
  }

  const resetErrors = () => {
    setErrorServerFeedback('');
    setErrorEmail('');
    setErrorPassword('');
    setErrorConfirmPassword('');
    setErrorFreelancerFirstName('');
    setErrorFreelancerLastName('');
    setErrorEmployerName('');
    setErrorEmployerType('');
  }

  const onChangeUserType = (userType: EUserType) => {
    const url = new URL(window.location.href);
    url.searchParams.set('type', userType);
    window.history.replaceState({}, '', url);
    setUserType(userType);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetErrors();
    const valid = validateFields();
    if (!valid) {
      return;
    }

    setLoading(true);
    if (userType === EUserType.EMPLOYER) {
      await signupEmployer(email, password).catch((error) => {
        setErrorServerFeedback(error);
        setLoading(false);
      });
      setUpdateUserObj({
        employerName: employerName,
        employerType: employerType,
        country: country
      });
    } else {
      await signupFreelancer(email, password).catch((error) => {
        setErrorServerFeedback(error);
        setLoading(false);
      });
      setUpdateUserObj({
        firstName: freelancerFirstName,
        lastName: freelancerLastName,
        country: country
      });
    }
  }

  useEffect(() => {
    const update = async () => {
      if (currentUser && currentUserType === userType && updateUserObj) {
        await updateCurrentUser(updateUserObj);

        if (currentUserType === EUserType.FREELANCER) {
          goToSignupUpdateProfile();
        }
      }
      setLoading(false);
    }
    update();
  }, [currentUser,
    currentUserType,
    updateUserObj])

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Blurred dots */}
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-primary-500/30 blur-3xl" />
      <div className="absolute -top-12 left-64 w-64 h-64 rounded-full bg-primary-700/25 blur-3xl" />
      <div className="absolute -bottom-10 -right-5 w-64 h-64 rounded-full bg-primary-500/20 blur-3xl" />
      <div className="absolute bottom-36 right-5 w-64 h-64 rounded-full bg-secondary-500/20 blur-3xl" />
      <div className="absolute bottom-12 left-12 w-48 h-48 rounded-full bg-primary-200/30 blur-3xl" />

      {/* Content container */}
      <div className="relative z-10 flex flex-col min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-5rem)] max-w-7xl mx-auto">
        <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <img
              alt="Your Company"
              src={BRAND_ASSETS.LOGO_SMALL_BLACK}
              className="mx-auto h-10 w-auto"
            />
            <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
              Create a new account
            </h2>
          </div>

          {/* Login Form */}
          <div className="flex flex-col -space-y-6 md:space-y-0 md:flex-row md:justify-center mt-10 mx-auto w-full md:max-w-[650px]">
            {/* User Type - Mobile */}
            <div className='z-10 md:hidden text-sm/6 grid grid-cols-2 gap-4 px-4'>
              <p className={` p-4 text-center font-bold cursor-pointer rounded-lg border border-b-2 transition-all duration-200
                ${userType === EUserType.FREELANCER
                  ? 'bg-primary-50 border-primary/30 border-b-primary shadow-md text-primary-600'
                  : 'bg-white text-gray-500 border-gray-400/30 border-b-gray-400'}`}
                onClick={() => onChangeUserType(EUserType.FREELANCER)}>
                I am a Freelancer</p>
              <p className={`p-4 text-center font-bold cursor-pointer rounded-lg border border-b-2 transition-all duration-200
                ${userType === EUserType.EMPLOYER
                  ? 'bg-primary-50 border-primary/30 border-b-primary shadow-md text-primary-600'
                  : 'bg-white text-gray-500 border-gray-400/30 border-b-gray-400'}`}
                onClick={() => onChangeUserType(EUserType.EMPLOYER)}>
                I have Projects</p>
            </div>

            {/* User Type - Desktop */}
            <div className='hidden -mr-10 md:grid z-10 text-sm/6 grid-cols-1 gap-4 px-4 pt-10 auto-rows-min'>
              <p className={`p-4 text-center font-bold cursor-pointer md:rounded-lg border border-r-2 transition-all duration-200
                ${userType === EUserType.FREELANCER
                  ? 'bg-primary-50 border-primary/30 border-r-primary shadow-md text-primary-600'
                  : 'bg-white text-gray-500 border-gray-400/30 border-r-gray-400'}`}
                onClick={() => onChangeUserType(EUserType.FREELANCER)}>
                I am a Freelancer</p>
              <p className={`p-4 text-center font-bold cursor-pointer md:rounded-lg border border-r-2 transition-all duration-200
                ${userType === EUserType.EMPLOYER
                  ? 'bg-primary-50 border-primary/30 border-r-primary shadow-md text-primary-600'
                  : 'bg-white text-gray-500 border-gray-400/30 border-r-gray-400'}`}
                onClick={() => onChangeUserType(EUserType.EMPLOYER)}>
                I have Projects</p>
            </div>

            {/* Form */}
            <div className="md:-mt-5 bg-white px-6 py-12 shadow-md border border-gray-100 md:rounded-lg md:px-12 flex-1">
              <form onSubmit={handleSubmit} className="space-y-4">

                {/* Email */}
                {userType && <TextInputField
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  autoComplete="email"
                  label="Email address"
                  error={errorEmail}
                />}

                {/* Password */}
                {userType && <TextInputField
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete="current-password"
                  label="Password"
                  error={errorPassword}
                  caption="Password requirements: 8 characters, at least 1 character and 1 number"
                />}

                {/* Confirm Password */}
                {userType && <TextInputField
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type="password"
                  autoComplete="current-password"
                  label="Confirm Password"
                  error={errorConfirmPassword}
                />}

                {/* Freelancer - First Name */}
                {userType === EUserType.FREELANCER && <TextInputField
                  value={freelancerFirstName}
                  onChange={(e) => setFreelancerFirstName(e.target.value)}
                  autoComplete="given-name"
                  label="First Name"
                  error={errorFreelancerFirstName}
                />}

                {/* Freelancer - Last Name */}
                {userType === EUserType.FREELANCER && <TextInputField
                  value={freelancerLastName}
                  onChange={(e) => setFreelancerLastName(e.target.value)}
                  autoComplete="family-name"
                  label="Last Name"
                  error={errorFreelancerLastName}
                />}

                {/* Employer - Name */}
                {userType === EUserType.EMPLOYER && <TextInputField
                  value={employerName}
                  onChange={(e) => setEmployerName(e.target.value)}
                  autoComplete="company-name"
                  label="Company Name"
                  error={errorEmployerName}
                />}

                {/* Employer - Type */}
                {userType === EUserType.EMPLOYER && <FlatDropdownMenu
                  options={getNumericEnumValues(EmployerType)}
                  optionsToTextMapper={UserEnumTextMapper.employerType}
                  onSelect={(value) => {
                    const val = value as unknown as EmployerType;
                    setEmployerType(val)
                  }}
                  defaultValue={EmployerType.COMPANY}
                  label="Type"
                />}

                {/* Country */}
                {userType && <FlatDropdownMenu
                  options={getStringEnumValues(ECountry)}
                  optionsToTextMapper={countriesToTextMapper}
                  onSelect={(value) => {
                    const val = value as unknown as ECountry;
                    setCountry(val)
                  }}
                  defaultValue={ECountry.UNITED_STATES}
                  label="Country"
                />}


                {/* Submit Button & Error Feedback*/}
                <div className='flex flex-col w-full items-center justify-center'>
                  {/* Submit Button */}
                  <FilledButton
                    isLoading={loading}
                    fullWidth
                    type='submit'
                  >Sign Up
                  </FilledButton>
                  {(errorServerFeedback?.length || 0) > 0 &&
                    <p className="text-center text-red-500">
                      {errorServerFeedback}
                    </p>}
                </div >
              </form>
            </div>
          </div>

          <p className="mt-10 text-center text-sm/6 text-gray-500">
            Already have an account?{' '}
            <a href={`${LOGIN_ROUTE}?type=${userType}`} className="font-semibold text-primary-600 hover:text-primary-500">
              Login
            </a>
          </p>
        </div>

      </div>
    </div>
  );
};


export default SignupPage;
